import * as React from "react";
import { Controller, useFormContext } from "react-hook-form";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import { Autocomplete as JoyAutocomplete, Typography } from "@mui/joy";
import { useProject } from "../hooks/query";
import { useEffect, useState } from "react";

interface Project {
    Id: string;
    Name: string;
    Description?: string;
    ProjectType?: string;
    CustomerName?: string;
    Status?: string;
}

interface ProjectSelectorProps {
    name: string;
    label: string;
    placeholder?: string;
    rules?: object;
    helperText?: string;
    size?: "sm" | "lg" | "md";
    helperType?: "tooltip" | undefined;
    disabled?: boolean;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
    name,
    label,
    placeholder = "Select project",
    rules = {},
    helperText,
    size = "md",
    helperType,
    disabled = false,
    ...props
}) => {
    const { control } = useFormContext();
    const { useList } = useProject();
    const listProjects = useList();

    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(false);

    // Fetch projects on component mount
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                setLoading(true);
                const response = await listProjects.mutateAsync();
                if (response && Array.isArray(response)) {
                    setProjects(response);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
                setProjects([]);
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    // Transform projects for autocomplete
    const projectOptions = projects.map((project) => ({
        value: project.Id,
        label: project.Name,
        project: project,
    }));

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error} size={size} sx={{ flex: 1 }}>
                    <FormLabel>{label}</FormLabel>
                    {helperText && !fieldState.error && (
                        <Typography level="body-xs" sx={{ color: "text.secondary", mb: 1, mt: -0.7 }}>
                            {helperText}
                        </Typography>
                    )}
                    {fieldState.error && (
                        <Typography level="body-xs" color="danger" sx={{ mb: 1, mt: -0.7 }}>
                            {fieldState.error.message}
                        </Typography>
                    )}
                    <JoyAutocomplete
                        {...field}
                        onChange={(e: any, value: any) => field.onChange(value)}
                        placeholder={placeholder}
                        disabled={disabled || loading}
                        loading={loading}
                        options={projectOptions}
                        getOptionLabel={(option) => {
                            if (typeof option === "string") return option;
                            return option?.label || "";
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (typeof option === "string" || typeof value === "string") {
                                return option === value;
                            }
                            return option?.value === value?.value;
                        }}
                        slotProps={{
                            listbox: {
                                sx: {
                                    zIndex: 10001,
                                },
                            },
                        }}
                        {...props}
                    />
                </FormControl>
            )}
        />
    );
};

export default ProjectSelector;
